import React, { useCallback, useEffect, useState } from 'react';

import { IndexIndicator } from '@/components/shared/IndexIndicator';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { usePermissions } from '@/providers/PermissionsProvider';
import { Capacitor } from '@capacitor/core';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { useSnackbar } from 'notistack';

import styles from './ReportCardCarousel.module.scss';

const isCapacitor = Capacitor.isNativePlatform();

export type ReportCardCarouselItem = {
	id: string;
	component: React.ReactNode;
};

type ReportCardCarouselProps = {
	reports: ReportCardCarouselItem[];
};

export const ReportCardCarousel: React.FC<ReportCardCarouselProps> = ({
	reports,
}) => {
	const [emblaRef, emblaApi] = useEmblaCarousel(
		{
			align: 'center',
			containScroll: false,
			loop: false,
		},
		isCapacitor ? [] : [WheelGesturesPlugin()],
	);
	const { hasPermission } = usePermissions();
	const { enqueueSnackbar } = useSnackbar();
	const { handleError } = useErrorHandler();

	const [selectedIndex, setSelectedIndex] = useState(0);
	const [anchorEl, setAnchorEl] = useState<Element | null>(null);

	const onIndexClick = useCallback(
		(index: number) => {
			if (!emblaApi) return;
			emblaApi.scrollTo(index);
		},
		[emblaApi],
	);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setSelectedIndex(emblaApi.selectedScrollSnap());
	}, [emblaApi, setSelectedIndex]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on('select', onSelect);
		emblaApi.on('reInit', onSelect);
	}, [emblaApi, onSelect]);

	return (
		<>
			<div className="embla" ref={emblaRef}>
				<div className="embla__container">
					{reports.map((report) => {
						return (
							<div
								className="embla__slide"
								key={`slide-${report.id}`}
							>
								{report.component}
							</div>
						);
					})}
				</div>
			</div>

			{reports.length > 1 && (
				<IndexIndicator
					className={styles['dots']}
					activeClassName={styles['active']}
					total={reports.length}
					current={selectedIndex}
					hasHover
					onIndicatorClick={onIndexClick}
				/>
			)}
		</>
	);
};
